import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { timeout } from 'ember-animated/-private/ember-scheduler';
import { restartableTask } from 'ember-concurrency';
import type FlowModel from 'garaje/models/flow';
import type ReactRendererService from 'garaje/services/react-renderer';
import type StateService from 'garaje/services/state';
import { GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST } from 'garaje/utils/roles';
import zft from 'garaje/utils/zero-for-tests';
import _uniqBy from 'lodash/uniqBy';
import type { Root } from 'react-dom/client';

interface UserSearchResult {
  userId: string;
  name: string;
  email: string;
  photo: string | null;
}

interface ApprovalChainsComponentSignature {
  Args: {
    flow: FlowModel;
    readOnly: boolean;
  };
}

interface ApprovalChainConfigurationCardProps {
  signInFlowId: string;
  locationId: string | null;
  readOnly: boolean;
  searchUsers: (term: string) => Promise<UserSearchResult[]>;
}

const SEARCH_ROLE_LIST = [GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST];

export default class ApprovalChainsComponent extends Component<ApprovalChainsComponentSignature> {
  @service declare reactRenderer: ReactRendererService;
  @service declare state: StateService;
  @service declare store: Store;

  private root?: Root;

  @action
  async injectComponent(): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { ApprovalChainConfigurationCard } = await import(
      'reactRichComponents/visitors/ApprovalChainConfigurationCard'
    );

    // prevent error from being thrown if element is already destroyed from navigating away
    if (!document.getElementById('react__visit-workflows-approval-chain-configuration-card')) {
      return;
    }

    this.root = await this.reactRenderer.render(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      ApprovalChainConfigurationCard,
      'react__visit-workflows-approval-chain-configuration-card',
      {
        signInFlowId: this.args.flow.id,
        locationId: this.state.currentLocation?.id ?? null,
        readOnly: this.args.readOnly,
        searchUsers: async (term: string): Promise<UserSearchResult[]> => {
          const users = await this.searchUsers.perform(term);

          return users.map((user) => ({
            userId: user.id,
            name: user.fullName,
            email: user.email,
            photo: user.userPhotoUrl || user.avatar || null,
          }));
        },
      } satisfies ApprovalChainConfigurationCardProps,
    );
  }

  /**
   * Task for searching for users that can be added as approval chain reviewers.
   * Searches for users with Global Admin, Location Admin, or Front Desk Admin/Receptionist roles.
   * If a current location is available, filters results to users in that location.
   *
   * NOTE: If you change the role checking logic here (SEARCH_ROLE_LIST), you must also update:
   * - app/pods/location-overview/sign-in-flows/show/security/controller.ts (searchUsers)
   * - app/pods/components/flows/sections/security/invite-approval/component.ts (searchUsersTask)
   */
  searchUsers = restartableTask(async (term: string) => {
    await timeout(zft(250));

    const filter: Record<string, string | boolean> = {
      query: term,
      roles: SEARCH_ROLE_LIST.join(','),
      confirmedUsers: true,
    };

    // Add location filter if current location is available
    if (this.state.currentLocation?.id) {
      filter['location'] = this.state.currentLocation.id;
    }

    const roles = await this.store.query('user-role', { filter });
    const users = await Promise.all(roles.map((role) => role.user));

    // Client-side filtering to ensure users match the search term
    const re = new RegExp(`.*${term}.*`, 'i');

    return _uniqBy(users, 'id') // dedup since multiple roles can point to same user
      .filter((user) => re.test(user.fullName)); // only return users whose name matches the search
  });

  @action
  destroyComponent(): void {
    if (this.root) {
      this.reactRenderer.unmount(this.root);
      this.root = undefined;
    }
  }
}
